/**
 * The document mutates the survey's write doors hand to `store.updateDocument`
 * — D-056.
 *
 * Each one is a function of the document the write LANDS in, and each says
 * `NO_CHANGE` when, read against that document, it has nothing to do. That is
 * what keeps a write that changes nothing from bumping `localRevision`,
 * dirtying the survey and queuing a push of an identical document (D-055's
 * review, follow-up 1). Pulled out of `SurveyProvider` and `AdHocList` so the
 * very mutate the app runs is the one the tests run.
 */

import type { AnswerValue, Scope, SurveyDocument } from '../../core/types';
import { NO_CHANGE, type DocumentMutate } from '../../db/store';
import { setDirtyPitCount } from './adHoc';
import {
  applyAnswerDefaults,
  applySurveyorAnswer,
  clearDefaultMark,
  isDefaultedKey,
  type PendingDefault,
} from './answerDefaults';
import { keyFor } from './results';

/**
 * A surveyor's answer. D-055: nothing is written to a question this document
 * no longer asks (a typed child committed as its parent's No hid it), and that
 * dropped write is NO_CHANGE.
 */
export function surveyorAnswerWrite(questionId: string, scope: Scope, value: AnswerValue): DocumentMutate {
  return (document) => (applySurveyorAnswer(document, questionId, scope, value) ? undefined : NO_CHANGE);
}

/**
 * Clear an answer, and its default mark with it. An answer that is not there,
 * with no mark, is NO_CHANGE.
 */
export function clearAnswerWrite(questionId: string, scope: Scope): DocumentMutate {
  return (document: SurveyDocument) => {
    const key = keyFor(questionId, scope);
    if (!(key in document.answers) && !isDefaultedKey(document, key)) return NO_CHANGE;
    delete document.answers[key];
    clearDefaultMark(document, key);
    return undefined;
  };
}

/**
 * Fill in the D-016 defaults. The presence re-check inside the transaction can
 * leave nothing to write (another write got there first): NO_CHANGE.
 */
export function defaultsWrite(pending: readonly PendingDefault[]): DocumentMutate {
  return (document) => (applyAnswerDefaults(document, pending) > 0 ? undefined : NO_CHANGE);
}

/** The Ad Hoc dirty-pits count: a count only, and only on a ticked row (D-055). */
export function dirtyPitCountWrite(count: number): DocumentMutate {
  return (document) => (setDirtyPitCount(document, count) ? undefined : NO_CHANGE);
}
